import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

// Logo putih (navbar transparan / di atas halaman)
const LOGO_WHITE = "https://yayasanwakaftozan.org/res/logo-putih.png";
// Logo original (navbar solid / saat scroll)
const LOGO_ORIGINAL = "https://yayasanwakaftozan.org/res/name.png";

const navLinks = [
  { name: "Beranda", path: "/" },
  { name: "Tentang Kami", path: "/tentang-kami" },
  { name: "Galeri Foto", path: "/galeri-foto" },
  { name: "Jurnal", path: "/jurnal" },
  { name: "Artikel", path: "/artikel" },
  { name: "Pendaftaran", path: "/informasi-pendaftaran" },
  { name: "Fasilitas", path: "/fasilitas-sekolah" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { waUrl } = useSettings();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const isHome = location.pathname === "/";
  // Transparan dengan teks putih hanya di homepage saat belum scroll
  const light = isHome && !scrolled && !isOpen;

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: light ? "transparent" : "rgba(255,255,255,0.97)",
        backdropFilter: light ? "none" : "blur(16px)",
        boxShadow: light ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
      }}
    >
      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-[60px] md:h-[64px]">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 flex-shrink-0">
            <img
              src={light ? LOGO_WHITE : LOGO_ORIGINAL}
              alt="Logo Yayasan Tozan"
              className="h-9 md:h-11 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    color: isActive ? (light ? "#d4af37" : "#198530") : (light ? "rgba(255,255,255,0.88)" : "#4b5563"),
                    backgroundColor: isActive ? (light ? "rgba(255,255,255,0.14)" : "#eef9f0") : "transparent",
                    fontWeight: isActive ? "700" : "500",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = light ? "white" : "#198530";
                      e.currentTarget.style.backgroundColor = light ? "rgba(255,255,255,0.12)" : "#f0fdf4";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = light ? "rgba(255,255,255,0.88)" : "#4b5563";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {link.name}
                </Link>
              );
            })}
            <div className="flex items-center gap-2 ml-3">
              <Link
                to="/informasi-pendaftaran"
                data-testid="nav-daftar-button"
                className="px-4 py-2 text-white rounded-full text-sm font-bold transition-all hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: "#198530" }}
              >
                Daftar Sekarang
              </Link>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-donasi-button"
                className="px-4 py-2 rounded-full text-sm font-bold transition-all hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: "#d4af37", color: "#044712" }}
              >
                Donasi
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg transition-colors"
            style={{ color: light ? "white" : "#374151" }}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden bg-white transition-all duration-300 overflow-hidden ${isOpen ? "max-h-[520px] shadow-xl" : "max-h-0"}`}
        style={{ borderTop: isOpen ? "1px solid #f0fdf4" : "none" }}
      >
        <div className="px-4 py-3 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
              className="flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-colors active:scale-95"
              style={{
                color: location.pathname === link.path ? "#198530" : "#374151",
                backgroundColor: location.pathname === link.path ? "#eef9f0" : "transparent",
                fontWeight: location.pathname === link.path ? "600" : "500",
              }}
            >
              {link.name}
            </Link>
          ))}
          <div className="grid grid-cols-2 gap-2 pt-2 pb-1">
            <Link
              to="/informasi-pendaftaran"
              className="flex items-center justify-center px-3 py-3 text-white rounded-xl text-sm font-bold text-center active:scale-95"
              style={{ backgroundColor: "#198530" }}
            >
              Daftar Sekarang
            </Link>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-donasi"
              className="flex items-center justify-center px-3 py-3 rounded-xl text-sm font-bold text-center active:scale-95"
              style={{ backgroundColor: "#d4af37", color: "#044712" }}
            >
              Donasi
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
